import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Button, EmptyState, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type { InboxNotification } from '@/lib/types';
import type { NotificationInboxValue } from '@/lib/use-notification-inbox';

/**
 * The "Notifications" tab of the bell panel: the in-app inbox.
 *
 * Rows are real `<Link>`s to the notification's `urlPath` (never a reload);
 * following one marks it read and closes the panel. A row without a usable
 * path is a button that only marks it read.
 *
 * ⚠️ `urlPath` comes from the server and is only followed when it is an
 * in-app path (`/x…`, never `//host`), so a bad row cannot send anyone off
 * the site.
 */

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto', style: 'short' });

const STEPS: { limit: number; unit: Intl.RelativeTimeFormatUnit; size: number }[] = [
  { limit: 60, unit: 'second', size: 1 },
  { limit: 3_600, unit: 'minute', size: 60 },
  { limit: 86_400, unit: 'hour', size: 3_600 },
  { limit: 604_800, unit: 'day', size: 86_400 },
  { limit: 2_629_800, unit: 'week', size: 604_800 },
  { limit: 31_557_600, unit: 'month', size: 2_629_800 },
  { limit: Number.POSITIVE_INFINITY, unit: 'year', size: 31_557_600 },
];

function relativeTime(iso: string, now: number): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '—';
  const seconds = Math.round((at - now) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return 'just now';
  for (const step of STEPS) {
    if (abs < step.limit) return RELATIVE.format(Math.round(seconds / step.size), step.unit);
  }
  return '—';
}

function inAppPath(path: string | null): string | null {
  if (!path) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  return path;
}

function RowBody({ item, now }: { item: InboxNotification; now: number }) {
  const unread = item.readAt === null;
  return (
    <>
      <span className="mt-1.5 flex w-2 shrink-0 justify-center" aria-hidden="true">
        {unread && <span className="h-2 w-2 rounded-full bg-accent-brand" />}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cx(
            'block text-sm text-fg [overflow-wrap:anywhere]',
            unread ? 'font-semibold' : 'font-medium',
          )}
        >
          {unread && <span className="sr-only">Unread: </span>}
          {item.title}
        </span>
        {item.bodyText && (
          <span className="mt-0.5 line-clamp-2 block text-xs text-fg-secondary [overflow-wrap:anywhere]">
            {item.bodyText}
          </span>
        )}
        <time
          dateTime={item.createdAt}
          title={formatDateTime(item.createdAt)}
          className="mt-1 block text-2xs text-fg-muted"
        >
          {relativeTime(item.createdAt, now)}
        </time>
      </span>
    </>
  );
}

const ROW =
  'flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors duration-150 ease-out hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none motion-reduce:transition-none max-sm:py-3';

export function InboxPanel({
  inbox,
  onNavigate,
}: {
  inbox: NotificationInboxValue;
  onNavigate: () => void;
}): ReactElement {
  const { items, loading, error, unreadCount, markRead, markAllRead } = inbox;
  // Read once per mount; the panel remounts every time the bell opens.
  const [now] = useState(() => Date.now());
  const hasUnread =
    (unreadCount !== null && unreadCount > 0) ||
    (items?.some((item) => item.readAt === null) ?? false);

  let body: ReactElement;
  if (items && items.length > 0) {
    body = (
      <ul className="divide-y divide-border">
        {items.map((item) => {
          const path = inAppPath(item.urlPath);
          return (
            <li key={item.id}>
              {path ? (
                <Link
                  to={path}
                  className={ROW}
                  onClick={() => {
                    markRead(item.id);
                    onNavigate();
                  }}
                >
                  <RowBody item={item} now={now} />
                </Link>
              ) : (
                <button type="button" className={ROW} onClick={() => markRead(item.id)}>
                  <RowBody item={item} now={now} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    );
  } else if (error) {
    body = (
      <div className="p-3">
        <Notice tone="danger" role="alert">
          Couldn&rsquo;t load notifications: {error}
        </Notice>
      </div>
    );
  } else if (loading || items === null) {
    body = (
      <div className="space-y-3 px-3 py-3" aria-busy="true">
        {[0, 1, 2].map((key) => (
          <div key={key} className="flex gap-2">
            <Skeleton className="mt-1 h-2 w-2 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  } else {
    body = <EmptyState title="No notifications" className="py-8" />;
  }

  return (
    <div>
      {hasUnread && (
        <div className="flex justify-end border-b border-border px-3 py-1.5">
          <Button variant="ghost" size="sm" onClick={markAllRead}>
            Mark all read
          </Button>
        </div>
      )}

      <div className="max-h-[min(26rem,60vh)] overflow-y-auto overscroll-contain">
        {body}
        {items && items.length > 0 && error && (
          <div className="px-3 pb-3">
            <Notice tone="danger" role="alert">
              Couldn&rsquo;t refresh notifications: {error}
            </Notice>
          </div>
        )}
      </div>

      <div className="border-t border-border px-3 py-2">
        <Link
          to="/notifications"
          onClick={onNavigate}
          className="flex items-center justify-between rounded-sm text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline max-sm:py-2"
        >
          <span>Notification settings</span>
          <span aria-hidden="true">&rarr;</span>
        </Link>
      </div>
    </div>
  );
}
